import { createContext, useContext, useEffect, useState, ReactNode, useCallback } from "react";
import { useAuth } from "@/context/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

interface WishlistContextType {
  wishlistIds: string[];
  isInWishlist: (productId: string) => boolean;
  toggleWishlist: (productId: string) => void;
  loading: boolean;
}

const WishlistContext = createContext<WishlistContextType | undefined>(undefined);

export const WishlistProvider = ({ children }: { children: ReactNode }) => {
  const { user } = useAuth();
  const [wishlistIds, setWishlistIds] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!user) {
      setWishlistIds([]);
      return;
    }
    const fetch = async () => {
      setLoading(true);
      const { data } = await supabase
        .from("wishlist")
        .select("product_id")
        .eq("user_id", user.id);
      if (data) setWishlistIds(data.map((d) => d.product_id));
      setLoading(false);
    };
    fetch();
  }, [user]);

  const isInWishlist = useCallback(
    (productId: string) => wishlistIds.includes(productId),
    [wishlistIds]
  );

  const toggleWishlist = useCallback(
    async (productId: string) => {
      if (!user) {
        toast.info("Sign in to save items to your wishlist");
        return;
      }

      if (isInWishlist(productId)) {
        setWishlistIds((prev) => prev.filter((id) => id !== productId));
        const { error } = await supabase
          .from("wishlist")
          .delete()
          .eq("user_id", user.id)
          .eq("product_id", productId);
        if (error) {
          setWishlistIds((prev) => [...prev, productId]);
          toast.error("Failed to remove from wishlist");
        } else {
          toast.success("Removed from wishlist");
        }
      } else {
        setWishlistIds((prev) => [...prev, productId]);
        const { error } = await supabase
          .from("wishlist")
          .insert({ user_id: user.id, product_id: productId });
        if (error) {
          setWishlistIds((prev) => prev.filter((id) => id !== productId));
          toast.error("Failed to add to wishlist");
        } else {
          toast.success("Added to wishlist ♥");
        }
      }
    },
    [user, isInWishlist]
  );

  return (
    <WishlistContext.Provider value={{ wishlistIds, isInWishlist, toggleWishlist, loading }}>
      {children}
    </WishlistContext.Provider>
  );
};

export const useWishlist = () => {
  const ctx = useContext(WishlistContext);
  if (!ctx) throw new Error("useWishlist must be used within WishlistProvider");
  return ctx;
};
